import { Modal, Input, InputNumber, Form, Alert, Select, Space, Tag, Typography, message } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateCaseBody } from '@/api/cases';
import { listSubjects } from '@/api/subjects';
import { listPolicyPacks } from '@/api/policy-packs';
import { listUsers } from '@/api/users_admin';
import { listEnterpriseStaff } from '@/api/assignments';
// Sprint 29:M:N 已删 — 资质直接从 pack.grants_qualification_id / qualification_name 推导,无需"再下拉"

/**
 * 新建申报案弹窗。
 *
 * 后端契约:POST /declaration-cases
 *   body: { subject_id, policy_pack_id, declare_year?, batch_code?, working_state? }
 *   - declare_year 与 batch_code 至少一个(schemas/declaration_case.py:check_year_or_batch)
 *   - policy_pack 必须是 active 状态(declaration_cases.py:204)
 *   - UNIQUE (tenant_id, subject_id, policy_pack_id, declare_year, batch_code) — 重复 409
 *
 * Sprint18-fix UX:
 *   - 主体 / 政策包改下拉(数据源 listSubjects + listPolicyPacks)
 *   - 申报年度默认今年
 *   - 批次代码加唯一性提示
 *   - Label 去掉括号英文字段名
 */
export interface CreateCaseModalProps {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (body: CreateCaseBody) => void;
  defaults?: {
    subject_id?: string;
    policy_pack_id?: string;
    declare_year?: number | null;
  };
}

interface FormValues {
  subject_id: string;
  policy_pack_id: string;
  declare_year?: number | null;
  batch_code?: string;
  /** S20-C0:负责人(可选 — 不传则后端建议为 enterprise primary staff) */
  owner_account_id?: string | null;
  // Sprint 29:declared_qualification_id 移除 — BE 已从 pack.grants_qualification_id 推导
}

export function CreateCaseModal({
  open,
  loading,
  onCancel,
  onSubmit,
  defaults,
}: CreateCaseModalProps) {
  const [form] = Form.useForm<FormValues>();
  const currentYear = new Date().getFullYear();
  const qc = useQueryClient();
  // Sprint 29:仍 watch policy_pack_id — 用于在表单内展示该 pack 授予的资质(只读)
  const selectedPackId = Form.useWatch('policy_pack_id', form);
  /** 记录最近一次"自动建议"指派给谁,防止用户改 subject 后又被无故覆盖手动选择 */
  const lastSuggestedRef = useRef<string | null>(null);

  // 主体下拉数据(企业列表 — 当前 listSubjects 走 /enterprises 兜底)
  const subjectsQuery = useQuery({
    queryKey: ['cases-modal', 'subjects'],
    queryFn: () => listSubjects({ page: 1, page_size: 100 }),
    enabled: open,
    staleTime: 30_000,
  });

  // 政策包下拉数据(仅 active)
  const packsQuery = useQuery({
    queryKey: ['cases-modal', 'policy-packs', 'active'],
    queryFn: () => listPolicyPacks({ status: 'active', page: 1, page_size: 100 }),
    enabled: open,
    staleTime: 30_000,
  });

  const subjectOptions = useMemo(
    () =>
      (subjectsQuery.data?.items ?? []).map((s) => ({
        value: String(s.id),
        label: s.name,
        creditCode: s.credit_code ?? null,
      })),
    [subjectsQuery.data],
  );

  // 候选 owner — 当前租户内可担任企服角色的账号(staff / tenant_admin)
  const usersQuery = useQuery({
    queryKey: ['cases-modal', 'users', 'owner-candidates'],
    queryFn: () => listUsers({ page: 1, page_size: 100 }),
    enabled: open,
    staleTime: 30_000,
  });

  const ownerOptions = useMemo(() => {
    const allowedRoles = new Set(['staff', 'tenant_admin', 'manager', 'platform_operator']);
    return (usersQuery.data?.items ?? [])
      .filter((u) => allowedRoles.has(u.role))
      .map((u) => ({
        value: u.id,
        label: u.username,
        role: u.role,
      }));
  }, [usersQuery.data]);

  const ownerNameOf = (id: string | null | undefined): string | null => {
    if (!id) return null;
    const hit = ownerOptions.find((o) => o.value === id);
    return hit?.label ?? null;
  };

  const packOptions = useMemo(
    () =>
      (packsQuery.data?.items ?? []).map((p) => ({
        value: String(p.id),
        label: p.display_name,
        version: p.version,
        category: p.category,
        // Sprint 29:BE 已 join qualification_name(单值);用于"选中 pack 后只读展示资质"
        qualificationName: p.qualification_name ?? null,
        qualificationId: p.grants_qualification_id ?? null,
      })),
    [packsQuery.data],
  );

  // Sprint 29:选中 pack 后,直接从 packOptions 拿 qualification_name 只读展示
  const selectedPackQualName = useMemo(() => {
    if (!selectedPackId) return null;
    const hit = packOptions.find((o) => o.value === selectedPackId);
    return hit?.qualificationName ?? null;
  }, [selectedPackId, packOptions]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      lastSuggestedRef.current = null;
      form.setFieldsValue({
        subject_id: defaults?.subject_id ?? '',
        policy_pack_id: defaults?.policy_pack_id ?? '',
        // Sprint18-fix:默认今年
        declare_year: defaults?.declare_year ?? currentYear,
        owner_account_id: undefined,
      });
    }
  }, [open, form, defaults, currentYear]);

  /**
   * S20-C0 自动建议链路:
   *   subject_id 变化 → fetch enterprise staff-assignments → 取 primary_account_id
   *   → setFieldValue('owner_account_id', primary) + message.info 提示
   * 仅当用户未手动改过(或新值仍等于上一次自动建议)时才覆盖,避免覆盖手选。
   */
  const handleSubjectChange = async (subjectId: string | undefined) => {
    if (!subjectId) return;
    try {
      const resp = await qc.fetchQuery({
        queryKey: ['enterprise-staff', subjectId],
        queryFn: () => listEnterpriseStaff(subjectId),
        staleTime: 30_000,
      });
      const primary = resp.primary_account_id;
      if (!primary) return;
      const current = form.getFieldValue('owner_account_id') as string | undefined;
      // 用户没手动改过 (当前空 / 等于上次建议) → 安全覆盖
      if (!current || current === lastSuggestedRef.current) {
        form.setFieldValue('owner_account_id', primary);
        lastSuggestedRef.current = primary;
        const name = ownerNameOf(primary) ?? '该企业主负责人';
        message.info(`已默认选 ${name} 为负责人,如需更改请下拉重选`);
      }
    } catch {
      // 取不到也不阻塞表单(可能企业没分配 primary)
    }
  };

  return (
    <Modal
      title="新建申报案"
      open={open}
      onCancel={onCancel}
      okText="创建"
      cancelText="取消"
      confirmLoading={loading}
      width={560}
      onOk={async () => {
        try {
          const v = await form.validateFields();
          if (v.declare_year == null && !(v.batch_code && v.batch_code.trim())) {
            form.setFields([
              {
                name: 'declare_year',
                errors: ['申报年度 与 批次代码 至少需提供一个'],
              },
            ]);
            return;
          }
          // Sprint 29:declared_qualification_id 由 BE 从 pack.grants_qualification_id 推导,
          //   FE 必须不传 — 若传值与 pack 不一致 BE 返 400(不会覆盖)
          onSubmit({
            subject_id: v.subject_id.trim(),
            policy_pack_id: v.policy_pack_id.trim(),
            declare_year: v.declare_year ?? null,
            batch_code: v.batch_code?.trim() || null,
            // S20-C0:owner 可选 — 不传后端会自动建议为 enterprise primary staff
            owner_account_id: v.owner_account_id?.trim() || null,
          });
        } catch {
          /* noop */
        }
      }}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message="申报年度 与 批次代码 至少填一个;同企业同政策包同年度同批次不可重复"
        style={{ marginBottom: 'var(--space-3)' }}
      />
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="subject_id"
          label="申报主体"
          rules={[{ required: true, message: '请选择企业' }]}
          extra="选择要为其申报的企业(若列表为空,请先在「主体管理」中录入或导入企业)"
        >
          <Select
            showSearch
            placeholder="搜索企业名称或信用代码"
            loading={subjectsQuery.isLoading}
            onChange={(val: string) => handleSubjectChange(val)}
            options={subjectOptions.map((o) => ({
              value: o.value,
              label: (
                <Space size={6}>
                  <span>{o.label}</span>
                  {o.creditCode && (
                    <Typography.Text type="secondary" style={{ fontSize: 'var(--font-size-xs)' }} code>
                      {o.creditCode}
                    </Typography.Text>
                  )}
                </Space>
              ),
              // 搜索同时匹配名称 + 信用代码
              searchValue: `${o.label} ${o.creditCode ?? ''}`,
            }))}
            optionFilterProp="searchValue"
            filterOption={(input, option) =>
              !!option?.searchValue?.toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={subjectsQuery.isLoading ? '加载中...' : '暂无企业'}
          />
        </Form.Item>

        <Form.Item
          name="policy_pack_id"
          label="政策包"
          rules={[{ required: true, message: '请选择政策包' }]}
          extra="仅显示 active 状态的政策包(平台运营方在「政策包配置」中激活)"
        >
          <Select
            showSearch
            placeholder="搜索政策包名称"
            loading={packsQuery.isLoading}
            options={packOptions.map((o) => ({
              value: o.value,
              label: (
                <Space size={6}>
                  <span>{o.label}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                    v{o.version} · {o.category}
                  </Typography.Text>
                </Space>
              ),
              searchValue: `${o.label} ${o.version} ${o.category}`,
            }))}
            optionFilterProp="searchValue"
            filterOption={(input, option) =>
              !!option?.searchValue?.toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={packsQuery.isLoading ? '加载中...' : '暂无 active 政策包'}
          />
        </Form.Item>

        {/* Sprint 29:申报资质 — 1 pack = 1 qualification,直接从 pack 推导(只读展示) */}
        <Form.Item
          label="申报资质"
          extra="本案的申报资质由所选政策包唯一决定 — 1 个政策包只授予 1 种资质;若需申报其它资质,请选对应的政策包"
        >
          {!selectedPackId ? (
            <Typography.Text type="secondary">请先选择政策包</Typography.Text>
          ) : selectedPackQualName ? (
            <Tag color="cyan">{selectedPackQualName}</Tag>
          ) : (
            <Typography.Text type="warning">
              该政策包未设置授予资质 — 请联系管理员补全 pack.grants_qualification_id
            </Typography.Text>
          )}
        </Form.Item>

        <Form.Item
          name="declare_year"
          label="申报年度"
          extra="默认当前年份;部分批次性申报可留空,改填批次代码"
        >
          <InputNumber
            min={2000}
            max={2100}
            placeholder={`默认 ${currentYear}`}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name="batch_code"
          label="批次代码"
          extra="申报年度二选一即可;同企业同政策包同年度同批次重复会被拒(409)"
        >
          <Input placeholder="如 2026-Q3-batch-A(选填)" />
        </Form.Item>

        <Form.Item
          name="owner_account_id"
          label="负责人 (案主负责人,可后续改派)"
          extra="选企业后自动建议该企业 primary 企服;留空则后端按企业 primary 自动指派"
        >
          <Select
            showSearch
            allowClear
            placeholder="选择案主负责人(可留空)"
            loading={usersQuery.isLoading}
            options={ownerOptions.map((o) => ({
              value: o.value,
              label: (
                <Space size={6}>
                  <span>{o.label}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 'var(--font-size-xs)' }}>
                    {o.role}
                  </Typography.Text>
                </Space>
              ),
              searchValue: `${o.label} ${o.role}`,
            }))}
            optionFilterProp="searchValue"
            filterOption={(input, option) =>
              !!option?.searchValue?.toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={usersQuery.isLoading ? '加载中...' : '暂无可用账号'}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
